import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { bins, dayName, formatDayLabel, messages, weekDays } from '@fruitways/shared';
import { Button, ConfirmDialog, Icon } from '@/components';
import { AppFrame } from '@/app/AppFrame';
import { ProfileMenu } from '@/app/ProfileMenu';
import { EMPTY_BINS, sum, useCapture } from '@/capture/CaptureContext';
import { useToast } from '@/hooks/useToast';
import { useMasterData } from '@/hooks/useMasterData';
import { AddVarietiesSheet } from './AddVarietiesSheet';
import './capture.css';

/** Step 2 of 3 (or edit): expected bins per day for one variety at a time. */
export function EntryScreen() {
  const nav = useNavigate();
  const master = useMasterData();
  const toast = useToast();
  const { draft, update, setBin, toggleVariety, addVarieties, hasUnsaved } = useCapture();
  const [gridOpen, setGridOpen] = useState(() => Boolean(draft?.editingId));
  const [addOpen, setAddOpen] = useState(false);
  const [removeCode, setRemoveCode] = useState<string | null>(null);

  if (!draft || !draft.farmId || draft.picked.length === 0)
    return <Navigate to="/capture" replace />;

  const isEdit = Boolean(draft.editingId);
  const farm = master?.farms.find((f) => f.id === draft.farmId);
  const order = master?.varieties.map((v) => v.code) ?? [];
  const addable = order.filter((c) => !draft.picked.includes(c));
  const active = draft.activeVariety ?? draft.picked[0]!;
  const pos = draft.picked.indexOf(active);
  const activeBins = draft.values[active] ?? EMPTY_BINS;
  const days = weekDays(draft.weekStart);
  const weekTotal = draft.picked.reduce((t, c) => t + sum(draft.values[c]), 0);

  function step(delta: number) {
    const next = draft!.picked[pos + delta];
    if (next) update({ activeVariety: next });
  }

  function requestRemove(code: string) {
    const done = toggleVariety(code, order);
    if (!done) setRemoveCode(code);
    else toast.show(messages.addVarieties.removed(code));
  }

  const back = () => nav(isEdit ? `/estimates/${draft.editingId}` : '/capture');

  return (
    <AppFrame
      title={isEdit ? messages.capture.editTitle : messages.capture.title}
      subtitle={farm ? messages.header.farmWeek(farm.name, draft.weekNumber) : undefined}
      onBack={back}
      action={<ProfileMenu unsaved={hasUnsaved} />}
    >
      <div className="fw-content" style={{ paddingBottom: 'var(--space-4)' }}>
        <span className="fw-secondary">
          {isEdit ? messages.capture.editingLine : messages.capture.step2}
        </span>

        <div className="fw-switcher">
          <button
            type="button"
            className="fw-switcher__arrow"
            onClick={() => step(-1)}
            disabled={pos <= 0}
            aria-label={messages.capture.previousVariety}
          >
            <Icon name="chevron-back" size={22} />
          </button>
          <button
            type="button"
            className="fw-switcher__current"
            onClick={() => setGridOpen((g) => !g)}
            aria-expanded={gridOpen}
          >
            {active}
            <span className="fw-secondary fw-tnum">
              {messages.capture.position(pos + 1, draft.picked.length)}
            </span>
            <Icon
              name={gridOpen ? 'chevron-up' : 'chevron-down'}
              size={18}
              color="var(--text-muted)"
            />
          </button>
          <button
            type="button"
            className="fw-switcher__arrow"
            onClick={() => step(1)}
            disabled={pos >= draft.picked.length - 1}
            aria-label={messages.capture.nextVariety}
          >
            <Icon name="chevron-forward" size={22} />
          </button>
        </div>

        {gridOpen && (
          <div className="fw-chip-grid">
            {draft.picked.map((code) => {
              const on = code === active;
              return (
                <button
                  key={code}
                  type="button"
                  className={`fw-chip fw-chip--tall${on ? ' fw-chip--on' : ''}`}
                  aria-pressed={on}
                  onClick={() => update({ activeVariety: code })}
                >
                  {code}
                  <span className="fw-chip__meta fw-tnum">{bins(sum(draft.values[code]))}</span>
                </button>
              );
            })}
            {isEdit && addable.length > 0 && (
              <button
                type="button"
                className="fw-chip fw-chip--tall fw-chip--add"
                aria-label={messages.addVarieties.addButtonLabel}
                onClick={() => setAddOpen(true)}
              >
                <Icon name="add" size={26} />
              </button>
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <span className="fw-label">{messages.common.variety}</span>
          <div className="fw-rows">
            <div className="fw-variety-card__head">
              <span className="fw-body-medium">{active}</span>
              {isEdit && (
                <button type="button" className="fw-remove" onClick={() => requestRemove(active)}>
                  {messages.common.remove}
                </button>
              )}
            </div>
            <div className="fw-variety-card__cols">
              <span className="fw-label">{messages.common.day}</span>
              <span className="fw-label">{messages.common.expectedBins}</span>
            </div>
            {days.map((iso, i) => (
              <div key={iso} className="fw-day-row">
                <span className="fw-day-row__label">
                  <span style={{ font: 'var(--type-body)' }}>{dayName(i)}</span>
                  <span className="fw-secondary fw-tnum">{formatDayLabel(iso)}</span>
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  placeholder="0"
                  aria-label={`${active} ${dayName(i)}`}
                  className="fw-bin-input fw-tnum"
                  value={activeBins[i] === 0 ? '' : activeBins[i]}
                  onChange={(e) =>
                    setBin(active, i, e.target.value === '' ? 0 : Number(e.target.value))
                  }
                />
              </div>
            ))}
            <div className="fw-total-row">
              <span className="fw-body-medium">{messages.capture.varietyTotal(active)}</span>
              <span className="fw-body-medium fw-tnum">{bins(sum(activeBins))}</span>
            </div>
          </div>
        </div>

        <div
          className="fw-total-row"
          style={{ borderRadius: 'var(--radius-md)', padding: 'var(--space-3) var(--space-4)' }}
        >
          <span className="fw-body-medium">{messages.capture.weekTotal}</span>
          <span className="fw-body-medium fw-tnum">{bins(weekTotal)}</span>
        </div>
      </div>

      <div className="fw-actions">
        <Button variant="secondary" icon="arrow-back" onClick={back}>
          {messages.common.back}
        </Button>
        <Button onClick={() => nav('/capture/review')}>{messages.common.review}</Button>
      </div>

      <AddVarietiesSheet
        open={addOpen}
        choices={addable}
        onClose={() => setAddOpen(false)}
        onAdd={(codes) => {
          addVarieties(codes, order);
          setAddOpen(false);
          toast.show(messages.addVarieties.added(codes));
        }}
      />

      <ConfirmDialog
        open={removeCode !== null}
        title={messages.dialogs.removeVariety.title}
        body={removeCode ? messages.dialogs.removeVariety.body(removeCode) : ''}
        note={removeCode ? messages.dialogs.removeVariety.note(removeCode) : undefined}
        confirmLabel={messages.dialogs.removeVariety.confirm}
        cancelLabel={messages.common.cancel}
        variant="danger"
        onCancel={() => setRemoveCode(null)}
        onConfirm={() => {
          if (removeCode) {
            toggleVariety(removeCode, order, true);
            toast.show(messages.addVarieties.removed(removeCode));
          }
          setRemoveCode(null);
        }}
      />
    </AppFrame>
  );
}
